import { useMemo } from 'react';
import type { AnalyticsEventName } from '@cbhq/client-analytics';
import { ActionType, ComponentType } from '@cbhq/client-analytics';

import { useIdCapture } from '../../context/IdCaptureContext';
import type { IdCaptureError, Track } from '../../types';
import { addIdCaptureRumError } from '../rum';

function isIdCaptureError<T>(error: unknown): error is IdCaptureError & T {
  return typeof error === 'object' && error !== null && 'message' in error;
}

export function errorToString(error: unknown): string {
  if (isIdCaptureError(error)) {
    return error.message;
  }

  return 'unknown-error';
}

export function makeErrorLogger(eventName: AnalyticsEventName, track: Track) {
  return (error: unknown, additionalContext?: Record<string, unknown>) => {
    track(eventName, {
      componentType: ComponentType.content_script,
      action: ActionType.measurement,
      error: errorToString(error),
      application_context: 'not_set',
      ...additionalContext,
    });
    addIdCaptureRumError(error instanceof Error ? error : new Error(errorToString(error)), {
      event: eventName,
    });
  };
}

export function createErrorLoggers(track: Track) {
  return {
    idvSubmit: (
      error: unknown,
      {
        context,
        statusCode,
        rawResponse,
        ...rest
      }: {
        context: 'post-onboarding' | 'onboarding' | 'onboarding-liquid';
        statusCode?: number | null;
        rawResponse?: string | null;
      },
    ) =>
      makeErrorLogger('verify_document_error', track)(error, {
        ...rest,
        context,
        status_code: statusCode,
        raw_response: rawResponse,
      }),
    idvProfilePoll: (error: unknown, isNullish: boolean) =>
      makeErrorLogger('verify_document_polling_error', track)(error, {
        is_nullish: isNullish,
      }),
    supportedDocs: (error: unknown) => makeErrorLogger('supported_docs_fetch_error', track)(error),
    idvProfiles: (error: unknown) => makeErrorLogger('idv_profiles_fetch_error', track)(error),
    idCaptureLogAnomaly: (error: unknown) =>
      makeErrorLogger('id_capture_flow_anomaly', track)(error),
    createSession: (error: unknown, isLatestApis: boolean) =>
      makeErrorLogger('id_capture_create_session_error', track)(error, {
        context: isLatestApis ? 'idv_latest_api' : 'vendor_onfido_api',
      }),
    getSession: (error: unknown) => makeErrorLogger('id_capture_get_session_error', track)(error),
    getTokenSession: (error: unknown) =>
      makeErrorLogger('id_capture_get_token_session_error', track)(error),
    submitSession: (error: unknown, isLatestApis: boolean) =>
      makeErrorLogger('id_capture_submit_session_error', track)(error, {
        context: isLatestApis ? 'idv_latest_api' : 'vendor_onfido_api',
      }),
    onfidoSdkError: (error: unknown, context: string) =>
      makeErrorLogger('id_capture_onfido-sdk-error', track)(error, {
        context,
      }),
    missingSdkVendor: (error: unknown, additionalContext?: Record<string, unknown>) =>
      makeErrorLogger('idv_sdk', track)(error, {
        context: 'create_session_missing_sdk_vendor',
        ...additionalContext,
      }),
  };
}

export type ErrorLoggers = ReturnType<typeof createErrorLoggers>;

export function useErrorLoggers(): ErrorLoggers {
  const { track } = useIdCapture();
  return useMemo(() => createErrorLoggers(track), [track]);
}
